import React, { useEffect, useState } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { IRouterConfig } from './types';

const RouteInstanceMap = {
  get(key: any) {
    return key._routeInternalComponent;
  },
  has(key: any) {
    return key._routeInternalComponent !== undefined;
  },
  set(key: any, value: any) {
    key._routeInternalComponent = value;
  },
};

function getCookie(name: any) {
  const reg = new RegExp(`(^| )${name}=([^;]*)(;|$)`);
  if (typeof window !== 'undefined') {
    const arr = document.cookie.match(reg);
    if (arr) {
      return unescape(arr[2]);
    }
  }
  return null;
}

// Support pass props from layout to child routes
const RouteWithProps = ({ path, exact, strict, render, location, sensitive, ...rest }: IRouterConfig) => (
  <Route
    path={path}
    exact={exact}
    strict={strict}
    location={location}
    sensitive={sensitive}
    render={(props: any) => render({ ...props, ...rest })}
  />
);

function withRoutes(route: any) {
  const { Routes = [], keepAlive, component } = route;

  if (RouteInstanceMap.has(route)) {
    return RouteInstanceMap.get(route);
  }

  let Component = (args: IRouterConfig) => {
    const { render, ...props } = args;
    return render(props);
  };

  if (component) {
    const OldComponent = Component;
    Component = props => {
      const [data, setData] = useState(typeof window !== 'undefined' ? (window as any).__FISHX_PAGE_PROPS__ : {});
      useEffect(() => {
        // When enter the page for the first time, need to use window.__ICE_PAGE_PROPS__ as props
        // And don't need to re-request to switch routes
        // Set the data to null after use, otherwise other pages will use
        async function fetchData() {
          if (typeof window !== 'undefined') {
            if ((window as any).__FISHX_PAGE_PROPS__) {
              (window as any).__FISHX_PAGE_PROPS__ = null;
            } else if (component.getInitialProps) {
              // When the server does not return data, the client calls getinitialprops
              (async () => {
                const pathname = window?.location?.pathname
                const locale = getCookie('userLocale')
                const ctx = { locale }
                const result = await component.getInitialProps({ pathname, ctx });
                setData(result);
              })();
            }
          }
        }
        fetchData();

      }, []);
      return (
        <OldComponent {...Object.assign({}, props, data)} />
      );
    }
  }

  if (Routes.length !== 0) {
    let len = Routes.length - 1;

    // 权限路由
    while (len >= 0) {
      const AuthRoute = Routes[len];
      const OldComponent = Component;
      Component = props => (
        <AuthRoute {...props}>
          <OldComponent {...props} />
        </AuthRoute>
      );
      len -= 1;
    }
  }

  // 缓存路由
  if (keepAlive && keepAlive.component) {
    const OldComponent = Component;
    Component = (props: any) => (
      <keepAlive.component
        saveScrollPosition={keepAlive.saveScrollPosition || true}
        name={props.match && props.match.params && props.match.params.id ? `${route.path.split(':')[0]}${props.match.params.id}` : route.path} id={props.match && props.match.params ? props.match.params.id : route.path}
      >
        <OldComponent {...props} />
      </keepAlive.component>
    );
  }

  const ret = (args: any) => {
    const { render, ...rest } = args;
    return (
      <RouteWithProps
        {...rest}
        render={(props: any) => {
          return <Component {...props} route={route} render={render} />;
        }}
      />
    );
  };
  RouteInstanceMap.set(route, ret);
  return ret;
}

export default function renderRoutes(routes: any, extraProps = {}, switchProps = {}) {
  return routes ? (
    <Switch {...switchProps}>
      {routes.map((route: IRouterConfig, i: number) => {
        if (route.redirect) {
          return (
            <Redirect
              key={route.key || i}
              from={route.path}
              to={route.redirect}
              exact={route.exact}
              strict={route.strict}
            />
          );
        }
        const RouteRoute = withRoutes(route);
        return (
          <RouteRoute
            key={route.key || i}
            path={route.path}
            exact={route.exact}
            strict={route.strict}
            sensitive={route.sensitive}
            render={(props: any) => {
              const childRoutes = renderRoutes(route.routes, extraProps, {
                location: props.location,
              });
              if (route.component) {
                const newProps = { route, ...props, ...extraProps, }
                let { component: Component } = route;
                return (
                  <Component {...newProps} route={route}>
                    {childRoutes}
                  </Component>
                );
              } else {
                return childRoutes;
              }
            }}
          />
        );
      })}
      <Route component={require('@tmp/404').default} />
    </Switch>
  ) : null;
}
